// ============================================================================
// Creator payout eligibility — full and partial
// ============================================================================
// Decides whether ops can record a payout on a hire, and what it should be.
//
// Two shapes:
//
//   FULL     — every required video landed and the hire is 'completed'. Pays the
//              hire's creator_rate_cents. The original rule, unchanged.
//   PARTIAL  — the creator delivered some videos but not all, and the deadline
//              (plus grace) is gone. Pays only for what arrived.
//
// The gate on a partial payout is the BRAND REFUND, not the hire's status:
//
//   Pay the creator for the videos the brand is still paying for.
//
// A refund is the only record that ops has settled the gap — a short hire keeps
// status 'confirmed' forever (wallet_refund_hire_to_credit deliberately doesn't
// touch status; see 20260805000000_wallet_refund_keeps_hire_status.sql), so
// status can't tell us whether the brand was made whole. Paying a creator for 3
// of 5 videos while the brand is still charged for 5 would quietly leave the
// brand short, which is why refund-first is a hard order here rather than a
// suggestion.
//
// Unlike refunds.ts, this module has no database counterpart to mirror: a payout
// is an admin UPDATE on cr_hires (payment_status/payment_amount_cents/...), with
// no RPC re-checking the rules. So THIS IS the rule, not a mirror of one — which
// is why the pay mutation calls it too instead of repeating its own guards.
// ============================================================================

import { isPastGracePeriod, graceHoursRemaining } from './deadlines'
import { usd } from './refunds'

export interface PayableHire {
  status: string
  payment_status: 'unpaid' | 'paid' | null
  /** Full-package rate owed to the creator. */
  creator_rate_cents: number
  videos_required: number
  package_size: number
  deadline_at: string | null
  /** Brand side — what was collected and what has been given back. */
  credit_applied_cents: number | null
  card_charged_cents: number | null
  refunded_credit_cents: number | null
  /** Trolley payout attempts (cr_hire_payouts), when the query joined them. */
  cr_hire_payouts?: TrolleyPayoutLike[] | null
}

export type PayoutMode = 'full' | 'partial'

/** The slice of a cr_hire_payouts row that payout eligibility needs. */
export interface TrolleyPayoutLike {
  status: string
  amount_cents: number
  /** Set when the payout went out in a weekly payout batch (cr_payout_batches). */
  payout_batch_id?: string | null
}

/**
 * Attempt states the database counts as LIVE — the partial unique index
 * cr_hire_payouts_one_live_per_hire and the manual-payment guard trigger use this
 * same set (20260924000001_trolley_creator_payouts.sql). 'processed' is in it
 * because a hire Trolley has paid must never be paid again, even in the moment
 * before the hire row itself reads 'paid'.
 */
export const LIVE_TROLLEY_PAYOUT_STATUSES: ReadonlyArray<string> = ['reserved', 'submitted', 'processed', 'needs_reconciliation']

/**
 * The Trolley payout currently holding this hire, if any. While one exists the
 * hire is still 'unpaid' (it flips on Trolley's 'processed'), so payment_status
 * alone would offer ops a second, duplicate payout.
 */
export function liveTrolleyPayout<T extends TrolleyPayoutLike>(hire: { cr_hire_payouts?: T[] | null }): T | null {
  return (hire.cr_hire_payouts ?? []).find(p => LIVE_TROLLEY_PAYOUT_STATUSES.includes(p.status)) ?? null
}

/**
 * The view of a submission that payout eligibility needs. Structured so the
 * Creator Payments table, the hire detail page and recordHirePayment all derive
 * `delivered`/`pending` from one function and can't drift.
 */
export interface PayoutSubmissionLike {
  review_status: 'approved' | 'rejected' | null
  deleted?: boolean
}

/**
 * Everything a hire's submissions say about payout eligibility.
 *
 * `delivered` matches refundState's definition (submitted, not admin-rejected,
 * not since deleted from TikTok), so a refund and the payout that follows it are
 * computed off the same delivery count and always reconcile.
 *
 * `pending` is the slice of delivered NOT yet explicitly approved. Every video
 * now needs an admin decision before a payout (no more auto-approve), so pending
 * > 0 blocks payout State-wide — the Pay button, the write, the bucket.
 */
export function payoutSubmissionCounts(subs: PayoutSubmissionLike[]): { delivered: number; pending: number } {
  const counted = subs.filter(v => v.review_status !== 'rejected' && !v.deleted)
  return {
    delivered: counted.length,
    pending: counted.filter(v => v.review_status !== 'approved').length,
  }
}

/**
 * No payout will ever be recorded on this hire, so an admin decision on its
 * videos can no longer move any money.
 *
 * Two ways to get here:
 *   - the creator was already paid; or
 *   - the brand was refunded IN FULL on a short delivery, which writes the job
 *     off (see brandFullyRefunded below).
 *
 * A full delivery is deliberately excluded even when fully refunded: the creator
 * did all the work, and a refund on a complete delivery is goodwill rather than a
 * write-off. Mirrors the same carve-out in cr_hires_close_engagement().
 *
 * This is the ONE definition of "closed" shared by three places that were each
 * deciding it separately, and disagreeing:
 *   - payoutState below, so the blocked label names the real reason instead of
 *     sending ops to review videos that can never unblock a payout;
 *   - opsBucket (hire-queue.ts), so a closed hire stops sitting in Needs review
 *     asking for an action nobody can take;
 *   - VideoReviewCell (HireDetail), which decides whether Approve/Reject render.
 *
 * Note what is NOT here: cr_hires.settled_at. HireDetail used to lock review on
 * it, but settled_at is stamped by ANY refund that closes a short hire —
 * including a PARTIAL one, which is exactly when the creator is still owed a
 * payout and that payout needs the review first. Locking there deadlocked the
 * money: review disabled, payout blocked on "Review pending", forever.
 * settled_at closes the CREATOR's window (no more submissions, free to
 * re-apply); it does not mean ops is finished.
 */
export function payoutClosed(hire: PayableHire, deliveredVideos: number): boolean {
  if ((hire.payment_status ?? 'unpaid') === 'paid') return true

  const requiredVideos = hire.videos_required ?? hire.package_size ?? 0
  const complete = requiredVideos > 0 && Math.max(0, deliveredVideos) >= requiredVideos
  if (complete) return false

  const collected = (hire.credit_applied_cents ?? 0) + (hire.card_charged_cents ?? 0)
  const refunded = hire.refunded_credit_cents ?? 0
  return collected > 0 && refunded >= collected
}

/**
 * Delivery is short and the creator still has time to finish — the deadline plus
 * its grace window has not run out.
 *
 * Null deadline counts as still running, deliberately: isPastGracePeriod(null) is
 * false, and a hire with no deadline should read as in-progress rather than
 * silently filed as done.
 *
 * Shared with opsBucket so the `delivering` bucket and the reason the Pay button
 * is off can't disagree about whether the creator is out of time.
 */
export function stillDelivering(hire: PayableHire, deliveredVideos: number): boolean {
  const requiredVideos = hire.videos_required ?? hire.package_size ?? 0
  if (requiredVideos <= 0) return false
  if (Math.max(0, deliveredVideos) >= requiredVideos) return false
  return !isPastGracePeriod(hire.deadline_at)
}

export interface PayoutState {
  /** Which payout this hire is shaped for, regardless of eligibility. null when
   *  the creator delivered nothing — there is no payout to shape. */
  mode: PayoutMode | null
  deliveredVideos: number
  undeliveredVideos: number
  requiredVideos: number
  /** Full-package creator rate. */
  fullRateCents: number
  /** Creator rate for one video of this package. Drives the partial amount. */
  perVideoCents: number
  /** What the creator earned: full rate on a full delivery, per-video x
   *  delivered on a partial one. The amount the dialog opens with. */
  earnedCents: number
  /** Held back because those videos never arrived (0 on a full payout). */
  withheldCents: number
  alreadyPaid: boolean
  eligible: boolean
  /** Why the pay button is unavailable, phrased as the thing ops needs to do
   *  next. null when eligible. */
  blockedReason: string | null
  /** Two-or-three-word form of blockedReason for the payments table's action
   *  column, which has no room for the sentence. Lives here so the glance-label
   *  and the tooltip can't tell ops two different stories. null when eligible. */
  blockedLabel: string | null
  /**
   * Video submissions counting toward the requirement that are NOT yet approved.
   * >0 blocks the payout: every video must have an explicit admin decision
   * before any money moves (no more auto-approve of unreviewed submissions).
   */
  pendingVideos: number
  // ── Brand-side context, so the dialog can explain the split it's part of ──
  brandCollectedCents: number
  brandRefundedCents: number
  /**
   * Brand got everything back, including the price of the videos that did arrive.
   * The job is written off, so a PARTIAL payout is blocked outright — there is no
   * hire revenue behind it and no delivered content the brand is still paying for.
   * A full delivery is unaffected: the creator did all the work, and our refund
   * rules won't refund a complete delivery in the first place.
   */
  brandFullyRefunded: boolean
}

/**
 * @param deliveredVideos videos counting toward the requirement — submitted, not
 *        admin-rejected, not since deleted from TikTok. Same definition
 *        refundState() takes, so a refund and the payout that follows it are
 *        computed off the same delivery count and always reconcile.
 * @param pendingVideos of THOSE videos still awaiting an admin decision. Default
 *        0 keeps callers that don't know about review status (tests, hire-queue)
 *        working; the pages and the write both pass it so a hire with unreviewed
 *        submissions is impossible to pay.
 */
export function payoutState(hire: PayableHire, deliveredVideos: number, pendingVideos = 0): PayoutState {
  const requiredVideos = hire.videos_required ?? hire.package_size ?? 0
  const delivered = Math.max(0, deliveredVideos)
  const undeliveredVideos = Math.max(0, requiredVideos - delivered)
  const complete = requiredVideos > 0 && delivered >= requiredVideos

  const fullRateCents = hire.creator_rate_cents ?? 0
  const perVideoCents = requiredVideos > 0 ? Math.round(fullRateCents / requiredVideos) : fullRateCents
  // Capped at the full rate so per-video rounding can never pay out more than
  // the package is worth.
  const earnedCents = complete ? fullRateCents : Math.min(fullRateCents, perVideoCents * delivered)
  const withheldCents = Math.max(0, fullRateCents - earnedCents)

  const brandCollectedCents = (hire.credit_applied_cents ?? 0) + (hire.card_charged_cents ?? 0)
  const brandRefundedCents = hire.refunded_credit_cents ?? 0
  const brandFullyRefunded = brandCollectedCents > 0 && brandRefundedCents >= brandCollectedCents

  const alreadyPaid = (hire.payment_status ?? 'unpaid') === 'paid'
  const mode: PayoutMode | null = delivered === 0 ? null : complete ? 'full' : 'partial'

  const blocked = ((): { reason: string; label: string } | null => {
    if (alreadyPaid) return { reason: 'This hire has already been paid', label: 'Paid' }

    // A Trolley payout is out and not yet confirmed. Second only to 'paid': the
    // money has (or may have) left, so no other rule matters until it resolves.
    // The database refuses a second payout here anyway; this is ops being told
    // why instead of being shown a Pay button that will fail.
    const inFlight = liveTrolleyPayout(hire)
    if (inFlight?.payout_batch_id) {
      // Sent as part of the weekly batch. Only a batch the admin CONFIRMED gets
      // here — a hire that is merely in a draft batch has no payout row and stays
      // payable on its own.
      return inFlight.status === 'needs_reconciliation'
        ? { reason: 'This hire is in a weekly payout batch that lost contact with Trolley mid-send — Sync the batch on the Payout Batches page before paying again', label: 'Batch: check' }
        : { reason: `This hire is included in a weekly payout batch that is processing in Trolley (${usd(inFlight.amount_cents)}) — it is marked paid once Trolley confirms delivery. If the payment fails, it can be paid again here.`, label: 'In payout batch' }
    }
    if (inFlight) {
      return inFlight.status === 'needs_reconciliation'
        ? { reason: 'A Trolley payout lost contact with Trolley mid-send — open the hire and Sync it before paying again', label: 'Trolley: check' }
        : { reason: `A ${usd(inFlight.amount_cents)} Trolley payout is in progress — the hire is marked paid once Trolley confirms delivery`, label: 'Trolley sending' }
    }
    if (delivered === 0) {
      return requiredVideos > 0
        ? {
            reason: `No videos delivered — there is nothing to pay for${brandRefundedCents > 0 ? ' (the brand has been refunded)' : ''}`,
            label: 'No videos yet',
          }
        : { reason: 'This hire has no video requirement', label: 'No requirement' }
    }

    // ── The job was written off ──────────────────────────────────────────────
    // A FULL refund on a short delivery gives the brand back everything it paid,
    // including the price of the videos that did arrive — so there is no hire
    // revenue left for a payout to come out of, and no partial content the brand
    // is still paying for. Nothing is payable, now or later.
    //
    // Checked BEFORE the review gate below, and that order is the whole point.
    // Reversed, a written-off hire reported 'Review pending': ops was told to go
    // approve videos, on a hire whose review buttons are gone precisely because
    // it is closed. The tab strip repeated it, parking the row in Needs review
    // asking for an action nobody can perform. Naming the real reason costs
    // nothing and stops the loop.
    //
    // Also checked before the `complete` branch, and scoped to short deliveries
    // by payoutClosed: a goodwill refund on a FULL delivery stays payable,
    // because the creator did all the work.
    if (payoutClosed(hire, delivered)) {
      return {
        reason: `The brand was refunded in full (${usd(brandRefundedCents)}) — this job was written off, so there is nothing to pay the creator from`,
        label: 'Fully refunded',
      }
    }

    // Delivery is short and the clock is still running, so nothing about this
    // hire is settled yet — not the refund, not the payout, and not whether the
    // videos already in are the final set.
    //
    // Above the review gate because while the creator can still deliver, an
    // unreviewed video is not what's holding the money up: the delivery is. Below
    // it, a hire 1 of 5 videos in reported 'Review pending', which reads as "ops
    // is the holdup" when ops has nothing to unblock yet.
    //
    // Requires a deadline: with none, stillDelivering is true by default and
    // there'd be no window to quote hours from, so fall through to the rules
    // below instead of inventing one.
    if (stillDelivering(hire, delivered) && hire.deadline_at) {
      const hours = graceHoursRemaining(hire.deadline_at)
      return {
        reason: `Creator can still deliver — ${hours}h left before the delivery window closes`,
        label: 'Awaiting delivery',
      }
    }

    // Every video must be APPROVED before any payout. pendingVideos is the slice
    // of delivered still waiting on a decision; money moves over unreviewed work
    // only by mistake, so this outranks every delivery/refund rule below.
    if (pendingVideos > 0) {
      return {
        reason: `${pendingVideos} submitted video${pendingVideos !== 1 ? 's' : ''} still need${pendingVideos !== 1 ? '' : 's'} approval — a payout can only be recorded once every video is approved`,
        label: 'Review pending',
      }
    }
    if (complete) {
      // Delivery is done but the hire hasn't been flipped to 'completed'. The
      // submission edge function does that automatically, so this means a
      // rejected/deleted video reverted it, or the row is stale.
      if (hire.status === 'completed') return null
      return {
        reason: `All ${requiredVideos} videos delivered but the hire is still '${hire.status}' — it must be completed before a full payout`,
        label: 'Not completed',
      }
    }

    // ── Partial ──────────────────────────────────────────────────────────────
    // A partial refund settles the gap and leaves the delivered videos funded, so
    // the creator is still owed for them. (The FULL-refund write-off is handled
    // by payoutClosed above, before the review gate.)
    if (brandRefundedCents > 0) return null

    // Only reachable with no deadline set — the window check above handles every
    // hire that has one.
    if (!hire.deadline_at) return { reason: 'No deadline set on this hire', label: 'No deadline' }

    // Nothing was ever collected (failed charge, comped hire), so there is no
    // refund to wait on — the delivery window closing is the only gate.
    if (brandCollectedCents <= 0) return null

    // The common blocker, and the one worth naming precisely: ops has to refund
    // the brand before the creator can be paid for a short delivery.
    return {
      reason: `Refund the brand for the ${undeliveredVideos} undelivered video${undeliveredVideos !== 1 ? 's' : ''} first — paying now would leave them charged ${usd(brandCollectedCents)} for ${delivered} of ${requiredVideos} videos`,
      label: 'Refund brand first',
    }
  })()

  return {
    mode,
    deliveredVideos: delivered,
    undeliveredVideos,
    requiredVideos,
    fullRateCents,
    perVideoCents,
    earnedCents,
    withheldCents,
    alreadyPaid,
    eligible: blocked === null,
    blockedReason: blocked?.reason ?? null,
    blockedLabel: blocked?.label ?? null,
    pendingVideos,
    brandCollectedCents,
    brandRefundedCents,
    brandFullyRefunded,
  }
}
